import {
  ChangeDetectorRef, Component, EventEmitter,
  forwardRef, HostListener, Input, OnDestroy, Output
} from '@angular/core';
import { NgIf } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { QueryPanelConnectorService } from '@myapp/modules/fwcore/services';

import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { searchIcon, SVGIcon } from "@progress/kendo-svg-icons";

import { Subject } from 'rxjs';

@Component({
  selector: 'query-panel-filter-box',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => QueryPanelFilterBoxComponent),
      multi: true
    }
  ],
  standalone: true,
  styleUrls: ['./query-panel-filter-box.component.scss'],
  templateUrl: './query-panel-filter-box.component.html',
  imports: [ButtonsModule, InputsModule, NgIf]
})

export class QueryPanelFilterBoxComponent
  implements ControlValueAccessor, OnDestroy {
  /*-------------------------- Input / Outputs --------------------------------*/

  @Input() placeholder: string = "";
  @Input() showSearchButton: boolean = false;

  @Output() onFilterChange = new EventEmitter<string>();

  /*-------------------------- Internal properties  ------------------*/

  protected destroy$ = new Subject();
  protected filterValue: string = "";
  protected isFilterFocus: boolean = false;
  protected searchSvg: SVGIcon = searchIcon;
  protected subscription: any;

  protected _onTouched: Function = () => {};
  protected _onChange: undefined | ((value: string) => void);

  /*-------------------------- Constructor  --------------------------*/
  constructor (
    private readonly _cdr: ChangeDetectorRef,
    protected readonly connectorService: QueryPanelConnectorService
  ) {
    // subscribe to events from other tree views / summary box
    this.subscription = this.connectorService.onMessage().subscribe(message => {
      // adds will have come from this or other treeviews, we can ignore them
      if (message) {
        switch (message.type) {
          case "clear":
            this.filterValue = "";
            this.onButtonClick();
            break;
          case "load":
            this.connectorService.isLoaded("_");
            break;
        }
      }
    });
  }

  /*-------------------------- ng --------------------------*/

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }
  /* -------------------------------  ControlValueAccessor ------------------------------- */

  public get value(): string {
    return this.filterValue;
  }

  public set value(value: string) {
    this.filterValue = value;
    this._cdr.markForCheck();
  }

  writeValue(value: string) {
    this.filterValue = value;
    this._cdr.markForCheck();
  }

  registerOnChange(fn: (_: any) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: any) {
    this._onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    // This method cannot be removed as it's required to satisfy the interface
  }

  /*-------------------------- Event handlers --------------------------*/

  onButtonClick() {
    this.onFilterChange.emit(this.filterValue);
  }

  onFilterTextChange(value: string) {
    if (value == "" && this.filterValue != "") {
      this.onFilterChange.emit("");
    }
    this.filterValue = value;
  }

  onFilterInputBlur() {
    this.isFilterFocus = false;
  }

  onFilterInputFocus() {
    this.isFilterFocus = true;
  }

  /*-------------------------- Host listeners --------------------------*/

  // Keyboard event: handles the "press enter to search" because Telerik
  // doesn't give us a keypress event for the kendoTextBox.
  @HostListener('document:keypress', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (this.isFilterFocus && event.key == "Enter") {
      this.onFilterChange.emit(this.filterValue);
    }
  }
}
